import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
	AppToastType,
	EventType,
	NotificationChannel,
	NotificationTargetAudience,
} from '@sportspredict/shared';
import {
	useGetLobbiesByEventIdForAdminQuery,
	useGetNotificationsQuery,
	useUpdateNotificationMutation,
	useCreateNotificationForEventMutation,
	useDeleteNotificationMutation,
} from '@/store/api';
import { useToast } from '@/components/ui/use-toast';
import { getEventParticipants } from '@/utils';
import { initialNotification, ROUTES } from '@/constants';

type ChannelKey = 'default' | 'push' | 'appToast';

const createInitialChannels = () => ({
	default: { enabled: false, expire_date: null },
	push: { enabled: false, expire_date: null },
	appToast: { enabled: false, expire_date: null, toastType: AppToastType.Info },
});

const createInitialScheduleTiming = () => ({
	sendImmediately: true,
	scheduledDate: null as Date | null,
});

const getNotificationLink = (event: any) => {
	if (!event?.id) return '';

	return event.type === EventType.Probability
		? `/probability-events/${event.id}/markets`
		: `${ROUTES.EVENT_DETAILS}?id=${event.id}`;
};

const createInitialNotification = (event: any, includeDefaultTitle = false) => ({
	...initialNotification,
	target_users: [],
	lobby_ids: [],
	title: includeDefaultTitle && event?.title ? `${event.title} Update` : '',
	link: getNotificationLink(event),
});

const arraysEqual = (left: string[], right: string[]) =>
	left.length === right.length && left.every((value, index) => value === right[index]);

const getErrorMessage = (error: unknown) =>
	error instanceof Error && error.message ? error.message : 'Failed to create notification';

export function useEventNotifications(event: any, lobbies: any[] = []) {
	const [channels, setChannels] = useState(createInitialChannels);
	const [newNotification, setNewNotification] = useState<any>(() =>
		createInitialNotification(event, true)
	);
	const [scheduleTiming, setScheduleTiming] = useState(createInitialScheduleTiming);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { toast } = useToast();

	const [updateNotification] = useUpdateNotificationMutation();
	const [createNotificationForEvent] = useCreateNotificationForEventMutation();
	const [deleteNotificationMutation] = useDeleteNotificationMutation();

	const { data: rawNotifications = [], isLoading } = useGetNotificationsQuery(
		{ event_id: event?.id },
		{ skip: !event?.id }
	);

	const notifications = useMemo(
		() =>
			[...rawNotifications].sort(
				(a: any, b: any) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime()
			),
		[rawNotifications]
	);

	const { data: adminLobbiesData } = useGetLobbiesByEventIdForAdminQuery(event?.id, {
		skip: !event?.id,
	});
	const adminLobbies: any[] = useMemo(() => adminLobbiesData ?? [], [adminLobbiesData]);

	const activeLobbies = useMemo(
		() =>
			Array.from(
				new Map(
					lobbies
						.filter((lobby) => Boolean(lobby?.id) && !lobby.deleted_date)
						.map((lobby) => [lobby.id, lobby])
				).values()
			),
		[lobbies]
	);
	const activeAdminLobbies = useMemo(() => {
		const activeLobbyIdSet = new Set(activeLobbies.map((lobby) => lobby.id));
		return adminLobbies.filter((lobby) => activeLobbyIdSet.has(lobby.id));
	}, [activeLobbies, adminLobbies]);

	const allParticipants = useMemo(
		() => getEventParticipants(activeAdminLobbies),
		[activeAdminLobbies]
	);

	const getParticipantsForScope = useCallback(
		(isEventWide: boolean, lobbyIds: string[]) => {
			if (isEventWide) return getEventParticipants(activeAdminLobbies);

			const selectedLobbyIdSet = new Set(lobbyIds);
			return getEventParticipants(
				activeAdminLobbies.filter((lobby) => selectedLobbyIdSet.has(lobby.id))
			);
		},
		[activeAdminLobbies]
	);

	const scopedParticipants = useMemo(
		() =>
			getParticipantsForScope(
				newNotification.is_event_wide !== false,
				newNotification.lobby_ids ?? []
			),
		[getParticipantsForScope, newNotification.is_event_wide, newNotification.lobby_ids]
	);

	const selectedChannelEntries = (
		[
			{ key: 'default', channel: NotificationChannel.Default },
			{ key: 'push', channel: NotificationChannel.Push },
			{ key: 'appToast', channel: NotificationChannel.AppToast },
		] as { key: ChannelKey; channel: NotificationChannel }[]
	).filter(({ key }) => channels[key].enabled);

	useEffect(() => {
		if (!event?.id) return;
		setChannels(createInitialChannels());
		setScheduleTiming(createInitialScheduleTiming());
		setNewNotification(createInitialNotification(event, true));
	}, [event?.id]);

	useEffect(() => {
		const activeLobbyIdSet = new Set(activeLobbies.map((lobby) => lobby.id));

		setNewNotification((previous: any) => {
			const isEventWide = activeLobbies.length === 0 ? true : previous.is_event_wide !== false;
			const lobbyIds = isEventWide
				? []
				: Array.from(new Set<string>(previous.lobby_ids ?? [])).filter((lobbyId) =>
						activeLobbyIdSet.has(lobbyId)
					);
			const participantSet = new Set(getParticipantsForScope(isEventWide, lobbyIds));
			const targetUsers = Array.from(new Set<string>(previous.target_users ?? [])).filter((email) =>
				participantSet.has(email)
			);

			if (
				previous.is_event_wide === isEventWide &&
				arraysEqual(previous.lobby_ids ?? [], lobbyIds) &&
				arraysEqual(previous.target_users ?? [], targetUsers)
			) {
				return previous;
			}

			return {
				...previous,
				is_event_wide: isEventWide,
				lobby_ids: lobbyIds,
				target_users: targetUsers,
			};
		});
	}, [activeLobbies, getParticipantsForScope]);

	const updateLobbyScope = (isEventWide: boolean, lobbyIds: string[] = []) => {
		const activeLobbyIdSet = new Set(activeLobbies.map((lobby) => lobby.id));
		const normalizedLobbyIds = isEventWide
			? []
			: Array.from(new Set(lobbyIds)).filter((lobbyId) => activeLobbyIdSet.has(lobbyId));
		const participantSet = new Set(getParticipantsForScope(isEventWide, normalizedLobbyIds));

		setNewNotification((previous: any) => ({
			...previous,
			is_event_wide: isEventWide,
			lobby_ids: normalizedLobbyIds,
			target_users: (previous.target_users ?? []).filter((email: string) =>
				participantSet.has(email)
			),
		}));
	};

	const toggleNotificationStatus = async (notification: any) => {
		try {
			await updateNotification({
				id: notification.id,
				is_active: !notification.is_active,
			}).unwrap();
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Failed to update notification status',
				className: 'bg-black border-0',
			} as any);
			console.error(error);
			throw new Error('Error updating notification status');
		}
	};

	const handleSubmit = async (formEvent: FormEvent) => {
		formEvent.preventDefault();
		setIsSubmitting(true);

		try {
			if (!newNotification.title?.trim() || !newNotification.message?.trim()) {
				throw new Error('Please fill in all required fields');
			}

			if (newNotification.is_event_wide === false && newNotification.lobby_ids.length === 0) {
				throw new Error('Please select at least one lobby');
			}

			if (!newNotification.is_global && newNotification.target_users.length === 0) {
				throw new Error('Please select at least one user to notify');
			}

			if (selectedChannelEntries.length === 0) {
				throw new Error('Please select at least one delivery channel');
			}

			if (!scheduleTiming.sendImmediately && !scheduleTiming.scheduledDate) {
				throw new Error('Please select a scheduled date');
			}

			const {
				target_users: targetUsers,
				target_audience: targetAudience,
				is_event_wide: isEventWideValue,
				lobby_ids: lobbyIds,
				is_global: isGlobal,
				...notificationFields
			} = newNotification;
			const isEventWide = isEventWideValue !== false;

			const results = await Promise.allSettled(
				selectedChannelEntries.map(({ key, channel }) =>
					createNotificationForEvent({
						...notificationFields,
						channel,
						expire_date: channels[key].expire_date ?? null,
						event_id: event.id,
						is_active: true,
						is_global: isGlobal,
						...(isGlobal
							? {
									target_audience: targetAudience ?? NotificationTargetAudience.AllUsers,
								}
							: { target_users: Array.from(new Set<string>(targetUsers)) }),
						is_event_wide: isEventWide,
						...(!isEventWide ? { lobby_ids: Array.from(new Set<string>(lobbyIds)) } : {}),
						...(channel === NotificationChannel.AppToast
							? { toast_type: channels.appToast.toastType }
							: {}),
						scheduled_date: !scheduleTiming.sendImmediately ? scheduleTiming.scheduledDate : null,
					}).unwrap()
				)
			);

			const succeededCount = results.filter((result) => result.status === 'fulfilled').length;
			const failedResults = results
				.map((result, index) => ({ result, key: selectedChannelEntries[index].key }))
				.filter(
					(entry): entry is { result: PromiseRejectedResult; key: ChannelKey } =>
						entry.result.status === 'rejected'
				);
			const failedChannelKeys = new Set(failedResults.map(({ key }) => key));
			const failureMessages = Array.from(
				new Set(failedResults.map(({ result }) => getErrorMessage(result.reason)))
			);

			if (succeededCount > 0) {
			}

			if (failedResults.length === 0) {
				setNewNotification(createInitialNotification(event));
				setScheduleTiming(createInitialScheduleTiming());
				setChannels(createInitialChannels());
				toast({
					title: 'Success',
					description: 'All notifications created successfully',
					className: 'bg-black border-0',
				} as any);
				return;
			}

			if (succeededCount === 0) {
				toast({
					variant: 'destructive',
					title: 'Error',
					description: failureMessages[0] ?? 'Failed to create notification',
					className: 'bg-black border-0',
				} as any);
				return;
			}
			setChannels((previous) => ({
				default: { ...previous.default, enabled: failedChannelKeys.has('default') },
				push: { ...previous.push, enabled: failedChannelKeys.has('push') },
				appToast: { ...previous.appToast, enabled: failedChannelKeys.has('appToast') },
			}));
			toast({
				variant: 'destructive',
				title: 'Partial success',
				description: `${succeededCount} created, ${failedResults.length} failed. ${
					failureMessages[0] ?? 'Retry the selected channel.'
				}`,
				className: 'bg-black border-0',
			} as any);
		} catch (error: unknown) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: getErrorMessage(error),
				className: 'bg-black border-0',
			} as any);
			console.error(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const deleteNotification = async (notificationId: string) => {
		try {
			await deleteNotificationMutation(notificationId).unwrap();

			toast({
				title: 'Success',
				description: 'Notification deleted successfully',
				className: 'bg-black border-0',
			} as any);
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Failed to delete notification',
				className: 'bg-black border-0',
			} as any);
			console.error(error);
			throw new Error('Error deleting notification');
		}
	};

	const isAppToastInvalid =
		channels.appToast.enabled &&
		![AppToastType.Info, AppToastType.Warning].includes(channels.appToast.toastType);
	const isAnyChannelSelected = selectedChannelEntries.length > 0;
	const isLobbyScopeInvalid =
		newNotification.is_event_wide === false && newNotification.lobby_ids.length === 0;

	return {
		notifications,
		isLoading,
		channels,
		setChannels,
		newNotification,
		setNewNotification,
		scheduleTiming,
		setScheduleTiming,
		activeLobbies,
		allParticipants,
		scopedParticipants,
		updateLobbyScope,
		isSubmitting,
		toggleNotificationStatus,
		handleSubmit,
		deleteNotification,
		isAppToastInvalid,
		isAnyChannelSelected,
		isLobbyScopeInvalid,
	};
}
